import type { Table as ReactTable } from "@tanstack/react-table";
import {
  type Range,
  type VirtualItem,
  type Virtualizer,
  defaultRangeExtractor,
  useVirtualizer,
} from "@tanstack/react-virtual";
import type React from "react";
import { useCallback, useMemo } from "react";

const isNotNull = <T,>(item: T | null | undefined): item is T => item != null;

interface VirtualGridOptions<TData> {
  gridRef: React.RefObject<HTMLDivElement>;
  table: ReactTable<TData>;
  measureRowHeight: (rowIndex: number) => number;
  defaultRowHeight: number;
  enableRowVirtualization?: boolean;
}

export interface VirtualGrid {
  virtualColumns: VirtualItem[];
  virtualRows: VirtualItem[];
  virtualPaddingLeft: number | undefined;
  virtualPaddingRight: number | undefined;
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  columnVirtualizer: Virtualizer<HTMLDivElement, Element>;
  measureGrid: () => void;
}

export const useVirtualGrid = <TData,>({
  gridRef,
  table,
  measureRowHeight,
  defaultRowHeight,
  enableRowVirtualization,
}: VirtualGridOptions<TData>): VirtualGrid => {
  const { rows: tableRows } = table.getRowModel();

  const allVisibleColumnsById = useMemo(
    () => new Map(table.getVisibleLeafColumns().map((c) => [c.id, c])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [table.getAllColumns(), table.getState().columnVisibility],
  );

  const { left: leftPinnedIds = [], right: rightPinnedIds = [] } =
    table.getState().columnPinning;

  const leftColumns = useMemo(
    () =>
      leftPinnedIds
        .map((id) => allVisibleColumnsById.get(id))
        .filter(isNotNull),
    [leftPinnedIds, allVisibleColumnsById],
  );

  const rightColumns = useMemo(
    () =>
      rightPinnedIds
        .map((id) => allVisibleColumnsById.get(id))
        .filter(isNotNull),
    [rightPinnedIds, allVisibleColumnsById],
  );

  const centerColumns = useMemo(
    () => table.getCenterVisibleLeafColumns(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      table.getAllColumns(),
      table.getState().columnVisibility,
      table.getState().columnOrder,
      table.getState().columnPinning,
    ],
  );

  const visibleColumns = useMemo(
    () => [...leftColumns, ...centerColumns, ...rightColumns],
    [leftColumns, centerColumns, rightColumns],
  );

  const pinnedColumnsIndices = useMemo(
    () => Array.from({ length: leftColumns.length }, (_, i) => i),
    [leftColumns.length],
  );

  const rightPinnedColumnIndices = useMemo(
    () =>
      Array.from(
        { length: rightColumns.length },
        (_, i) => leftColumns.length + centerColumns.length + i,
      ),
    [leftColumns.length, centerColumns.length, rightColumns.length],
  );

  const estimateSize = useCallback(
    (index: number) => {
      const column = visibleColumns[index];
      if (!column) {
        return 0;
      }
      const size = column.getSize();
      const actualSize = table.getState().columnSizing[column.id];
      return actualSize ?? size;
    },
    [visibleColumns, table],
  );

  const columnVirtualizer = useVirtualizer({
    count: visibleColumns.length,
    getScrollElement: () => gridRef.current,
    estimateSize: estimateSize,
    rangeExtractor: useCallback(
      (range: Range) => {
        const columnIndices = defaultRangeExtractor(range);
        const allIndices = Array.from(
          new Set([
            ...pinnedColumnsIndices,
            ...rightPinnedColumnIndices,
            ...columnIndices,
          ]),
        );
        allIndices.sort((a, b) => a - b);
        return allIndices;
      },
      [pinnedColumnsIndices, rightPinnedColumnIndices],
    ),
    horizontal: true,
    overscan: 3,
  });

  const rowVirtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => gridRef.current,
    estimateSize: () => defaultRowHeight,
    overscan: 3,
    enabled: enableRowVirtualization,
    measureElement: (element) => {
      const rowIndexRaw = element?.getAttribute("data-dataset-index");
      const rowIndex = rowIndexRaw != null ? parseInt(rowIndexRaw, 10) : null;
      if (rowIndex == null || !isFinite(rowIndex)) {
        return defaultRowHeight;
      }

      return measureRowHeight(rowIndex);
    },
  });

  const measureGrid = useCallback(() => {
    Array.from(rowVirtualizer.elementsCache.values()).forEach((el) =>
      rowVirtualizer.measureElement(el),
    );
    columnVirtualizer.measure();
  }, [rowVirtualizer, columnVirtualizer]);

  const virtualColumns = columnVirtualizer.getVirtualItems();
  const virtualRows = rowVirtualizer.getVirtualItems();

  return useMemo(() => {
    let virtualPaddingLeft: number | undefined;
    let virtualPaddingRight: number | undefined;

    if (columnVirtualizer && virtualColumns?.length) {
      const virtualItemsByIndex = new Map(
        virtualColumns.map((vc) => [vc.index, vc]),
      );

      const leftPinnedIndexSet = new Set(pinnedColumnsIndices);
      const rightPinnedIndexSet = new Set(rightPinnedColumnIndices);

      const firstNonLeftPinnedItem = virtualColumns.find(
        (vc) => !leftPinnedIndexSet.has(vc.index),
      );
      const lastNonRightPinnedItem = [...virtualColumns]
        .reverse()
        .find((vc) => !rightPinnedIndexSet.has(vc.index));

      const totalSize = columnVirtualizer.getTotalSize();

      const firstNonLeftPinnedIndex = firstNonLeftPinnedItem?.index ?? 0;

      const leftPinnedWidth = pinnedColumnsIndices
        .filter((colIndex) => colIndex < firstNonLeftPinnedIndex)
        .reduce((acc, colIndex) => {
          const item = virtualItemsByIndex.get(colIndex);
          return acc + (item?.size ?? 0);
        }, 0);

      virtualPaddingLeft = Math.max(
        0,
        (firstNonLeftPinnedItem?.start ?? 0) - leftPinnedWidth,
      );

      const lastNonRightPinnedIndex =
        lastNonRightPinnedItem?.index ?? visibleColumns.length - 1;

      const rightPinnedWidth = rightPinnedColumnIndices
        .filter((colIndex) => colIndex > lastNonRightPinnedIndex)
        .reduce((acc, colIndex) => {
          const item = virtualItemsByIndex.get(colIndex);
          return acc + (item?.size ?? 0);
        }, 0);

      virtualPaddingRight = Math.max(
        0,
        totalSize -
          (lastNonRightPinnedItem?.end ?? totalSize) -
          rightPinnedWidth,
      );
    }

    return {
      virtualColumns,
      virtualRows,
      virtualPaddingLeft,
      virtualPaddingRight,
      rowVirtualizer,
      columnVirtualizer,
      measureGrid,
    };
  }, [
    virtualColumns,
    virtualRows,
    rowVirtualizer,
    columnVirtualizer,
    pinnedColumnsIndices,
    rightPinnedColumnIndices,
    measureGrid,
    visibleColumns.length,
  ]);
};
